import React, { useEffect } from 'react'
import { View, StyleSheet, Dimensions, TouchableOpacity } from 'react-native'
import { Text, YStack, Spinner, XStack, isWeb } from 'tamagui'
import { Button } from '@components/Button'
import { useLocalSearchParams, router } from 'expo-router'
import { usePdfStore } from '@stores/pdfStore'
import { AppLayout } from '@layouts/AppLayout'
import { PdfWrapper } from '@components/PdfWrapper'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { HeroIcon } from '@components/HeroIcon'

export default function PDFViewerScreen() {
  const { url } = useLocalSearchParams()

  // Obtém o estado do PDF da store
  const {
    isLoaded,
    numberOfPages,
    currentPage,
    error,
    pdfSource,
    setCurrentPage,
    setError,
  } = usePdfStore()

  const safeArea = useSafeAreaInsets()

  const windowWidth = Dimensions.get('window').width

  // Decodifica a URL se for necessário
  const decodedUrl = url ? decodeURIComponent(url as string) : ''

  useEffect(() => {
    // Se ocorrer erro na tela anterior, limpa o erro ao montar esta tela
    if (error) {
      setError(null)
    }
  }, [error, setError])

  const handlePageChanged = (
    page: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _totalPages: number,
  ) => {
    setCurrentPage(page)
  }

  const handleError = (errorObj: Error | unknown) => {
    if (errorObj instanceof Error) {
      setError(errorObj.message)
    } else {
      setError('Erro desconhecido ao carregar o PDF')
    }
  }

  const handleGoBack = () => {
    router.back()
  }

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#000',
    },
    pdf: {
      width: windowWidth,
      height: '100%',
      minHeight: '100%',
      backgroundColor: '#f0f0f0',
    },
    header: {
      width: '100%',
      height: 56,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: '#000',
      paddingHorizontal: 16,
    },
    backButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      justifyContent: 'center',
      alignItems: 'center',
    },
    title: {
      flex: 1,
      color: '#fff',
      fontSize: 18,
      fontWeight: 'bold',
      marginLeft: 16,
    },
    pageIndicator: {
      position: 'absolute',
      top: 16,
      left: 16,
      height: 40,
      color: '#6E6E6E',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 8,
      borderRadius: 8,
      backgroundColor: '#DFDFDFB8',
    },
    errorContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#111',
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#111',
    },
  })

  if (isWeb) {
    return (
      <AppLayout
        backRoute={{
          label: 'Voltar',
        }}
      >
        <YStack
          flex={1}
          justifyContent="center"
          alignItems="center"
          padding="$spacing-inset-md"
        >
          <Text fontSize={18}>
            Visualização de PDF não disponível na versão web
          </Text>
          {decodedUrl && (
            <TouchableOpacity
              onPress={() => window.open(decodedUrl, '_blank')}
              style={{
                marginTop: 16,
                padding: 12,
                backgroundColor: '#0066cc',
                borderRadius: 8,
              }}
            >
              <Text color="white">Abrir PDF no navegador</Text>
            </TouchableOpacity>
          )}
        </YStack>
      </AppLayout>
    )
  }

  if (!pdfSource && !decodedUrl) {
    return (
      <View style={styles.errorContainer}>
        <Text color="$red-900">URL inválida</Text>
      </View>
    )
  }

  if (error) {
    return (
      <YStack flex={1}>
        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
            <HeroIcon
              icon={['fal', 'chevron-left']}
              color="$primary-300"
              size={24}
            />
          </TouchableOpacity>
          <Text style={styles.title}>Erro</Text>
        </View>
        <View style={styles.errorContainer}>
          <Text color="$red-900">Erro ao carregar o PDF: {error}</Text>
        </View>
      </YStack>
    )
  }

  // Se ainda não carregou, mostra indicador de carregamento
  if (!isLoaded) {
    return (
      <YStack flex={1}>
        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
            <HeroIcon icon={['fal', 'chevron-left']} color="#fff" size={24} />
          </TouchableOpacity>
          <Text style={styles.title}>Carregando PDF</Text>
        </View>
        <View style={styles.loadingContainer}>
          <Spinner size="large" color="$blue-500" />
          <Text color="white" marginTop={16}>
            Carregando o documento...
          </Text>
        </View>
      </YStack>
    )
  }

  return (
    <>
      <XStack
        w="100%"
        alignItems="center"
        h={60 + safeArea.top}
        paddingTop={safeArea.top}
        backgroundColor="$neutral-0"
      >
        <Button onPress={handleGoBack} variant="secondary" borderWidth={0}>
          <Text color="$neutral-900">Voltar</Text>
        </Button>
      </XStack>
      <YStack flex={1}>
        <View style={styles.container}>
          <PdfWrapper
            pdfSource={pdfSource}
            handlePageChanged={handlePageChanged}
            handleError={handleError}
            currentPage={currentPage}
            decodedUrl={decodedUrl}
            styles={styles}
          />

          <View style={styles.pageIndicator}>
            <Text>
              {currentPage} de {numberOfPages}
            </Text>
          </View>
        </View>
      </YStack>
    </>
  )
}
